import { useState } from 'react';
import {
  useFetchAllIcxInfoQuery,
  BasicInfoFragment,
  FetchAllIcxInfoQuery,
  IndustrialCompanyScale,
  IcxRegion,
  IndustrialComplexName,
  IndustrialSectorsCategory,
  IndustrialSectorsName,
  IndustrialBizSearchType,
  CapaSortType,
  Maybe
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import {
  Box,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead
} from '@mui/material';
import { ArrElement } from '@utils/types';
import { FilterList } from '@components/Header/useFilterList';
import { IndustryLandDetailDrawer } from './IndustryLandDetailDrawer';
import { StyledTableCell, StyledTableRow } from '@components/StyledTable';
import {
  allIcxInfoParamsDTO,
  basicCityToString,
  removeStringPrevDot,
  fullinvestigationCompanyScaleToString,
  employees2020FullTimeTotalToString
} from './utils';
import { ExpandMore, ExpandLess, HorizontalRule } from '@mui/icons-material';

export type AllIcxInfo = ArrElement<
  NonNullable<
    FetchAllIcxInfoQuery['fetchAllIcxInfo']['data']
  >['icxDetatilModelList']
>;

const LIMIT = 14;

type IndustryParamsType = {
  page: string;
  [FilterList.Region]: IcxRegion[];
  [FilterList.CompanyScale]: IndustrialCompanyScale[];
  [FilterList.ComplexName]: IndustrialComplexName[];
  [FilterList.SectorsCategory]: IndustrialSectorsCategory[];
  [FilterList.SectorsName]: IndustrialSectorsName[];
  q: string;
  employeesCountSortType: Maybe<CapaSortType>;
  searchType: Maybe<IndustrialBizSearchType>;
};

const IndustryLandList = () => {
  const [drawer, setDrawer] = useState<{
    open: boolean;
    data?: AllIcxInfo;
  }>({
    open: false
  });

  const { onUpdateQueryString, query } = useQueryString<IndustryParamsType>();
  const page = Number(query.page ?? 1);
  const employeesCountSortType = query.employeesCountSortType ?? null;

  const { data: allData, loading } = useFetchAllIcxInfoQuery({
    variables: {
      input: {
        offset: (page - 1) * LIMIT,
        searchKeyword: query.q,
        limit: LIMIT,
        icxRegion:
          allIcxInfoParamsDTO(query[FilterList.Region]) ??
          Object.values(IcxRegion),
        companyScale:
          allIcxInfoParamsDTO(query[FilterList.CompanyScale]) ??
          Object.values(IndustrialCompanyScale),
        industrialComplexList:
          allIcxInfoParamsDTO(query[FilterList.ComplexName]) ??
          Object.values(IndustrialComplexName),
        industryCategory:
          allIcxInfoParamsDTO(query[FilterList.SectorsCategory]) ??
          Object.values(IndustrialSectorsCategory),
        sectors:
          allIcxInfoParamsDTO(query[FilterList.SectorsName]) ??
          Object.values(IndustrialSectorsName),
        industrialBizSearchType: query?.searchType,
        employeesCountSortType
      }
    }
  });

  const handleMoveDetailPage = (
    id: string | null | undefined,
    data: AllIcxInfo
  ) => {
    if (!id) {
      return;
    }
    setDrawer({ open: true, data });
  };

  const handleClickSortType = () => {
    const nextSortType =
      employeesCountSortType === null
        ? CapaSortType.Desc
        : employeesCountSortType === CapaSortType.Desc
        ? CapaSortType.Asc
        : null;
    onUpdateQueryString({ employeesCountSortType: nextSortType });
  };

  const totalCount = allData?.fetchAllIcxInfo.data?.icxDetatilModelCount ?? 0;
  const renderList = allData?.fetchAllIcxInfo.data?.icxDetatilModelList ?? [];
  const restCount = LIMIT - renderList.length;

  const SortIcon = getSortIcon(employeesCountSortType);

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>업체명</StyledTableCell>
              <StyledTableCell>지역</StyledTableCell>
              <StyledTableCell>단지</StyledTableCell>
              <StyledTableCell>제조업</StyledTableCell>
              <StyledTableCell>업종</StyledTableCell>
              <StyledTableCell>회사 규모</StyledTableCell>
              <StyledTableCell>
                <Box
                  onClick={handleClickSortType}
                  sx={{
                    display: 'flex',
                    ':hover': {
                      cursor: 'pointer'
                    }
                  }}
                >
                  직원수
                  {SortIcon}
                </Box>
              </StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {renderList.map(({ id, ...rest }) => (
              <CompanyListItem
                key={id}
                onClick={() => handleMoveDetailPage(id, rest)}
                {...rest}
              />
            ))}
          </TableBody>
        </Table>
        {Array(restCount < 0 ? 0 : restCount)
          .fill(0)
          .map((_, index) =>
            loading ? (
              index % 2 ? (
                <Box height={40} key={index} />
              ) : (
                <Skeleton key={index} variant={'rectangular'} height={40} />
              )
            ) : (
              <Box height={40} key={index} />
            )
          )}
      </TableContainer>
      <Box
        padding={2}
        width={'100%'}
        display={'flex'}
        justifyContent={'center'}
      >
        <Pagination
          count={Math.ceil((totalCount ?? 0) / LIMIT)}
          page={page}
          color={'primary'}
          onChange={(e, value) => {
            onUpdateQueryString({ page: `${value}` });
          }}
        />
      </Box>
      <IndustryLandDetailDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false })}
        detailInfo={drawer.data}
      />
    </>
  );
};

export default IndustryLandList;

interface CompanyListItemProps extends BasicInfoFragment {
  onClick: () => void;
}

const CompanyListItem = ({
  onClick,
  basicCity,
  basicCompanyName,
  basicComplexName,
  basicIndustrial,
  basicIndustryCategory,
  fullinvestigationCompanyScale,
  employees2020FullTimeTotal
}: CompanyListItemProps) => (
  <StyledTableRow
    onClick={() => onClick()}
    sx={{
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: '#00000030'
      }
    }}
  >
    <StyledTableCell>{basicCompanyName}</StyledTableCell>
    <StyledTableCell>{basicCityToString(basicCity)}</StyledTableCell>
    <StyledTableCell>{basicComplexName}</StyledTableCell>
    <StyledTableCell>
      {removeStringPrevDot(basicIndustryCategory)}
    </StyledTableCell>
    <StyledTableCell>{removeStringPrevDot(basicIndustrial)}</StyledTableCell>
    <StyledTableCell>
      {fullinvestigationCompanyScaleToString(fullinvestigationCompanyScale)}
    </StyledTableCell>
    <StyledTableCell>
      {employees2020FullTimeTotalToString(employees2020FullTimeTotal)}
    </StyledTableCell>
  </StyledTableRow>
);

const getSortIcon = (employeesCountSortType: Maybe<CapaSortType>) => {
  if (!employeesCountSortType) {
    return <HorizontalRule />;
  }
  if (employeesCountSortType === CapaSortType.Desc) {
    return <ExpandMore />;
  }
  return <ExpandLess />;
};
